import { useState } from 'react'
import { Modal, Input, Avatar, Typography, Button, Checkbox, Empty, theme } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { CHAT_DRAWER_Z_INDEX } from '../ChatDrawer'

const { Text } = Typography

interface AddParticipantsModalProps {
  chatId: string
  open: boolean
  onClose: () => void
}

/**
 * Вибір нових учасників (кнопка «Додати» у «Про чат», тільки власник).
 * Показує лише користувачів, яких у чаті ще немає — додавання на беку
 * ідемпотентне, але навіщо пропонувати те, що вже є.
 */
const AddParticipantsModal = observer(({ chatId, open, onClose }: AddParticipantsModalProps) => {
  const { chatStore, userStore } = useStore()
  const { token } = theme.useToken()
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const chat = chatStore.chats.find((c) => c.id === chatId)
  if (!chat) return null

  const q = query.toLowerCase().trim()
  const candidates = userStore.users.filter(
    (u) =>
      !chat.participantIds.includes(u.id) &&
      (q === '' || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q))
  )

  const toggle = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const handleClose = () => {
    setQuery('')
    setSelectedIds([])
    onClose()
  }

  const handleAdd = async () => {
    if (selectedIds.length === 0) return
    setIsSubmitting(true)
    try {
      await chatStore.addParticipants(chatId, selectedIds)
    } finally {
      setIsSubmitting(false)
      handleClose()
    }
  }

  return (
    <Modal
      title="Додати учасників"
      open={open}
      onCancel={handleClose}
      zIndex={CHAT_DRAWER_Z_INDEX + 1}
      footer={
        <Button type="primary" block disabled={selectedIds.length === 0} loading={isSubmitting} onClick={() => void handleAdd()}>
          Додати {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
        </Button>
      }
    >
      <Input
        placeholder="Пошук людей..."
        prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />
      <div
        className="chat-scroll"
        style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {candidates.length === 0 ? (
          <Empty description="Нікого додати" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          candidates.map((user) => (
            <div
              key={user.id}
              onClick={() => toggle(user.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 4px',
                cursor: 'pointer',
                borderRadius: 6,
              }}
            >
              <Checkbox checked={selectedIds.includes(user.id)} />
              <Avatar src={user.avatarUrl} size={32}>
                {user.firstName.charAt(0)}
              </Avatar>
              <Text style={{ fontSize: 13 }}>
                {user.firstName} {user.lastName}
              </Text>
            </div>
          ))
        )}
      </div>
    </Modal>
  )
})

export default AddParticipantsModal
